import type { AgentStreamState } from "./state.svelte.js";
import type { SyncSession } from "./sync-session.js";
import { journalRun } from "./transcript-journal.js";
import { reportMalformedFrame } from "./malformed-frame.js";
import { newerWindows } from "./newer-window.svelte.js";
import type { AgentEvent, AgentStreamPayload, TurnEnd } from "./types.js";

type FrameSeqs = Pick<AgentStreamPayload, "seq" | "timelineSeq" | "timelineLife" | "workSessionId" | "turnEnd">; // prettier-ignore

// A turn's end as the wire names it; undefined for an older daemon (none sent) or a malformed
// value (reported).
export function turnEndOf(raw: unknown): TurnEnd | undefined {
  if (raw === undefined) return undefined;
  const end = readTurnEnd(raw);
  if (!end) reportMalformedFrame("turnEnd");
  return end;
}

function readTurnEnd(raw: unknown): TurnEnd | undefined {
  if (typeof raw !== "object" || raw === null) return undefined; // null: typeof null is "object"
  const { lastSeq, life } = raw as { lastSeq?: unknown; life?: unknown }; // cast: checked below
  if (typeof lastSeq !== "number" || !Number.isSafeInteger(lastSeq) || lastSeq < 0)
    return undefined;
  return typeof life === "string" && life ? { lastSeq, life } : undefined;
}

/** Committed rows the live stream skipped: every seq strictly between `after` and `before`. */
export interface TimelineGap {
  after: number;
  before: number;
  // The server's own cut (pageHoleIsServerCut): read back, not reported.
  cut?: boolean;
}

// A frame's committed row seq; never `seq`, which the relay turns into the workspace counter.
export function committedTimelineSeq(payload: FrameSeqs): { seq: number } | undefined {
  const seq = payload.timelineSeq;
  return typeof seq === "number" && Number.isSafeInteger(seq) && seq >= 0 ? { seq } : undefined;
}

// The row a frame at `seq` follows: rows a read merged count, or the next live row after a reopen
// reads as a gap past them. Scanned only for a frame that seems to skip one (the live path's cost),
// and never across lives: another life's rows on screen must not hide a skip in this one.
function rowsReached(
  state: Pick<AgentStreamState, "committedHighWater" | "highestHeldSeq" | "lifeDiffers">,
  agentId: string,
  seq: number,
  life: string | undefined,
): number | undefined {
  const high = state.committedHighWater(agentId);
  if (high === undefined || seq <= high + 1 || state.lifeDiffers(agentId, life)) return high;
  return Math.max(high, state.highestHeldSeq(agentId, true) ?? 0);
}

// The listener's work on one frame; the gap when its row skipped past the next one.
export function applyAgentStreamFrame(
  state: Pick<
    AgentStreamState,
    | "handleEvent"
    | "noteCommittedSeq"
    | "committedHighWater"
    | "highestHeldSeq"
    | "lifeDiffers"
    | "noteRowSession"
    | "noteLiveRow"
    | "isCachePainted"
    | "isMirrorHydrated"
  >,
  agentId: string,
  event: AgentEvent,
  payload: FrameSeqs,
): TimelineGap | undefined {
  const committed = committedTimelineSeq(payload);
  // A forward window stops short of the tail: its rows would land below a hole. The reader
  // pages down to them; the page that reaches the tail reopens the live path (newer-window).
  if (event.type === "timeline" && newerWindows.has(agentId)) {
    journalRun(agentId, "live", "held-newer", committed?.seq);
    return undefined;
  }
  const before = committed
    ? rowsReached(state, agentId, committed.seq, payload.timelineLife)
    : undefined;
  // The row's work session, which the session view projects on (an older daemon sends none).
  if (committed && event.type === "timeline" && payload.workSessionId !== undefined) {
    // null: the wire's "row of no session".
    state.noteRowSession(agentId, committed.seq, payload.workSessionId ?? null);
  }
  // The end frame carries its turnEnd so the sweep already knows whether rows are owed (B4).
  state.handleEvent(agentId, event, committed, readTurnEnd(payload.turnEnd));
  if (!committed) {
    journalRun(agentId, "live", event.type === "timeline" ? "delta" : event.type, undefined);
    return undefined;
  }
  state.noteCommittedSeq(agentId, committed.seq, payload.timelineLife);
  if (event.type === "timeline" && payload.timelineLife) {
    state.noteLiveRow(agentId, committed.seq, payload.timelineLife, event.item);
  }
  // Over a disk or mirror paint a skip is only that copy's age: the open's page answers the tail.
  const preview = state.isCachePainted(agentId) || state.isMirrorHydrated(agentId);
  const gap =
    before !== undefined && committed.seq > before + 1 && !preview
      ? { after: before, before: committed.seq }
      : undefined;
  journalRun(agentId, "live", gap ? "row-after-gap" : "row", committed.seq);
  return gap;
}

// Whether a live frame may be mirrored into session `sessionId`'s body: only one the daemon
// attributed to it. One agent can serve several sessions (reuse), so an unattributed frame
// (an older daemon's turn_*) could be another session's; it is dropped, and the body catches
// up from its turn-boundary page (knowledge/126 steps 1-2).
export function frameForSession(
  payload: { workSessionId?: string | null },
  sessionId: string,
): boolean {
  return payload.workSessionId === sessionId;
}

// Mirrors one live frame of the session's agent into the session body `key`, if it is this
// session's timeline row. The single place SessionWorkView's listener writes the body.
// Without the row's seq: session pages carry none (seq is per-agent, attachRowTimestamps), and
// a seq'd row in a seq-less body was seated at the page's first divider, above newer prompts.
export function mirrorSessionFrame(
  state: Pick<AgentStreamState, "handleEvent">,
  key: string,
  session: { id: string; agentId: string },
  payload: Pick<AgentStreamPayload, "agentId" | "event" | "workSessionId">,
): void {
  if (payload.agentId !== session.agentId || !frameForSession(payload, session.id)) return;
  if (
    payload.event.type !== "timeline" &&
    payload.event.type !== "image_rewrite" &&
    !payload.event.type.startsWith("turn_")
  )
    return;
  state.handleEvent(key, payload.event);
}

// Which of the session's agents a live frame belongs to, as mirrorSessionFrame's `session`.
// Normally one — session.agentId. On a COLD resume there is a second: the agent the spawn
// just returned, which starts streaming (the user's own prompt first) before the roster
// re-read moves session.agentId onto it. Its frames used to be dropped until a reload.
export function sessionMirrorOwner(
  sessionId: string,
  agentIds: readonly (string | null | undefined)[],
  frameAgentId: string,
): { id: string; agentId: string } | null {
  return agentIds.includes(frameAgentId) ? { id: sessionId, agentId: frameAgentId } : null;
}

interface PendingGap extends TimelineGap {
  current: () => boolean;
}

// One read-back per agent per window. A gap inside the window widens the pending range and
// is read back at the window's end, unless its workspace or stream has moved on by then.
export function throttledGapRecovery(
  recover: (agentId: string, gap: TimelineGap) => void,
  windowMs = 5_000,
  now: () => number = Date.now,
  schedule: (fn: () => void, ms: number) => void = (fn, ms) => void setTimeout(fn, ms),
  // The scope that resets its per-agent windows and held ranges (#3629).
  owner?: SyncSession,
): (agentId: string, gap: TimelineGap, current: () => boolean) => void {
  const last = new Map<string, number>();
  const pending = new Map<string, PendingGap>();
  owner?.collection("gapRecoveryWindows", last);
  owner?.collection("gapRecoveryPending", pending);
  const run = (agentId: string, gap: PendingGap) => {
    last.set(agentId, now());
    const range = { after: gap.after, before: gap.before };
    if (gap.current()) recover(agentId, gap.cut === true ? { ...range, cut: true } : range);
  };
  return (agentId, gap, current) => {
    const since = now() - (last.get(agentId) ?? -Infinity);
    if (since >= windowMs) return run(agentId, { ...gap, current });
    const held = pending.get(agentId);
    if (held) {
      // A held range from a stream that moved on is dropped, never revived by `current`.
      // A widened range is a cut only when both halves are.
      const widened = held.current()
        ? {
            after: Math.min(held.after, gap.after),
            before: Math.max(held.before, gap.before),
            cut: held.cut === true && gap.cut === true,
          }
        : gap;
      pending.set(agentId, { ...widened, current });
      return;
    }
    pending.set(agentId, { ...gap, current });
    schedule(() => {
      const due = pending.get(agentId);
      pending.delete(agentId);
      if (due) run(agentId, due);
    }, windowMs - since);
  };
}
